import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "@/utils/utils";
import { useEffect, useRef, useState } from "react";
import { DrawerResizeContext } from "@/components/ui/drawer-resize-state";

// Width kept clear on the left when no offset is passed: an open app sidebar.
const DEFAULT_SIDEBAR_OFFSET = 208;
const DRAWER_RIGHT_OFFSET = 25;

// vaul 1.x doesn't forward `modal` to Radix Dialog; patches/vaul+*.patch
// restores it. Without it, `modal={false}` drawers opened by prop lock the page.
const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-surface-elevation1", className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

interface ResizeState {
  isHoveringEdge: boolean;
  isResizing: boolean;
}

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  initialWidth?: number;
  variant?: "default" | "chat";
  isSettingsDrawer?: boolean;
  renderContent?: (resizeState: ResizeState) => React.ReactNode;
  onResizeEnd?: (newWidth: number) => void;
  /** Width in px of whatever sits on the page's left edge; the drawer never resizes over it. */
  sidebarOffset?: number;
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      children,
      initialWidth = 500,
      variant = "default",
      isSettingsDrawer = false,
      renderContent,
      onResizeEnd,
      sidebarOffset = DEFAULT_SIDEBAR_OFFSET,
      ...props
    },
    ref
  ) => {
    const [width, setWidth] = useState(initialWidth);
    const [isResizing, setIsResizing] = useState(false);
    const [isHoveringEdge, setIsHoveringEdge] = useState(false);
    const contentRef = useRef<React.ElementRef<typeof DrawerPrimitive.Content> | null>(null);

    const setContentRefs = React.useCallback(
      (node: React.ElementRef<typeof DrawerPrimitive.Content> | null) => {
        contentRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<React.ElementRef<typeof DrawerPrimitive.Content> | null>).current = node;
        }
      },
      [ref]
    );

    // Clamp width when sidebar state changes
    useEffect(() => {
      const currentMaxWidth =
        window.innerWidth - sidebarOffset - DRAWER_RIGHT_OFFSET - 10;
      if (width > currentMaxWidth) {
        setWidth(currentMaxWidth);
      }
    }, [sidebarOffset, width]);

    // Track width in ref for onResizeEnd callback
    const widthRef = useRef(width);
    widthRef.current = width;

    useEffect(() => {
      const onMouseMove = (e: MouseEvent) => {
        if (!isResizing) return;
        const newWidth = window.innerWidth - e.clientX;
        // Dynamic max width based on sidebar state
        const currentMaxWidth =
          window.innerWidth - sidebarOffset - DRAWER_RIGHT_OFFSET - 10;
        setWidth(Math.max(420, Math.min(newWidth, currentMaxWidth)));
      };

      const onMouseUp = () => {
        if (isResizing) {
          onResizeEnd?.(widthRef.current);
        }
        setIsResizing(false);
      };

      if (isResizing) {
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      }

      return () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };
    }, [isResizing, sidebarOffset, onResizeEnd]);

    // Increased edge detection area for easier resizing (15px on each side of the border)
    const EDGE_DETECTION_WIDTH = 15;

    const handleMouseDown = (e: React.MouseEvent) => {
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        if (Math.abs(e.clientX - rect.left) <= EDGE_DETECTION_WIDTH) {
          setIsResizing(true);
        }
      }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        setIsHoveringEdge(
          Math.abs(e.clientX - rect.left) <= EDGE_DETECTION_WIDTH
        );
      }
    };

    return (
      <DrawerPortal>
        <DrawerResizeContext.Provider value={{ isHoveringEdge, isResizing }}>
          <DrawerPrimitive.Content
            ref={setContentRefs}
            className={cn(
              variant === "chat"
                ? "bg-transparent flex flex-col rounded-t-[10px] fixed outline-none"
                : "bg-surface-elevation1 flex flex-col fixed outline-none shadow-drawer",
              variant !== "chat" &&
                "rounded-2xl border-[0.5px] border-border-border1",
              "z-50 flex flex-col items-start p-0 gap-0 right-0 min-w-40",
              variant === "default" && "pb-5",
              !isSettingsDrawer && "h-[91vh] top-16",
              isSettingsDrawer && "h-[96.5vh] top-4",
              "after:hidden right-4",
              variant === "default" && "p-0",
              className
            )}
            style={{
              width: `${width}px`,
              cursor: isResizing || isHoveringEdge ? "ew-resize" : "auto"
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setIsHoveringEdge(false)}
            {...props}
          >
            {variant !== "default" && (
              <div className="mx-auto max-w-md bg-surface-elevation1" />
            )}
            {renderContent
              ? renderContent({ isHoveringEdge, isResizing })
              : children}
          </DrawerPrimitive.Content>
        </DrawerResizeContext.Provider>
      </DrawerPortal>
    );
  }
);
DrawerContent.displayName = "DrawerContent";

interface DrawerHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Bottom border between the header and the drawer body. */
  divider?: boolean;
}

const DrawerHeader = ({
  className,
  divider = true,
  ...props
}: DrawerHeaderProps) => (
  <div
    className={cn(
      "flex w-full items-center justify-between gap-4 h-16 p-4",
      divider && "border-b-[0.5px] border-border-border1",
      className
    )}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

interface DrawerFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Pads the footer and spaces its children, for footers whose buttons bring no spacing of their own. */
  padded?: boolean;
}

const DrawerFooter = ({
  className,
  padded = false,
  ...props
}: DrawerFooterProps) => (
  <div
    className={cn(
      "mt-auto flex flex-col w-full",
      padded && "gap-2 p-4",
      className
    )}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "flex flex-row items-center gap-4 text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

interface DrawerDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> {
  /** Shows the description on screen; by default it is read only by screen readers. */
  show?: boolean;
}

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  DrawerDescriptionProps
>(({ className, show = false, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn(
      "pl-4 text-base dark:text-text-drawer-description",
      !show && "sr-only",
      className
    )}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription
};
